// ============================================================
// App.tsx — 應用程式根元件
// 管理整體導航狀態、登入狀態、頁面路由
// ============================================================

import React, { FC, useState, useEffect } from 'react';
import PostureCamera      from 'components/PostureCamera';
import ProgressDashboard  from 'components/ProgressDashboard';
import LearningModuleView from 'components/LearningModule';
import ExerciseGuide      from 'components/ExerciseGuide';
import { Staff, PostureSessionPayload, AIFeedbackResponse } from 'types/posture';
import { loginStaff }     from 'api/client';
import { usePostureSession } from 'hooks/usePostureSession';

// ── 頁面類型 ─────────────────────────────────────────────
type Page = 'camera' | 'progress' | 'learning' | 'exercises';

// ── 導航設定 ─────────────────────────────────────────────
const NAV_ITEMS: { page: Page; label: string; icon: string }[] = [
    { page: 'camera',    label: '姿勢評估',  icon: '📸' },
    { page: 'progress',  label: '我的進度',  icon: '📊' },
    { page: 'learning',  label: '學習課程',  icon: '📚' },
    { page: 'exercises', label: '矯正運動',  icon: '🧘' },
];

// ── 登入頁面 ─────────────────────────────────────────────
interface LoginPageProps {
    onLogin: (staff: Staff, token: string) => void;
}

const LoginPage: FC<LoginPageProps> = ({ onLogin }) => {
    const [employeeId, setEmployeeId] = useState<string>('EMP001');
    const [password,   setPassword]   = useState<string>('Staff@12#$');
    const [error,      setError]      = useState<string>('');
    const [loading,    setLoading]    = useState<boolean>(false);

    async function handleLogin(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        if (!employeeId || !password) {
            setError('請輸入員工工號及密碼');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const { token, staff } = await loginStaff(employeeId, password);
            // 將 Token 存入 localStorage 供後續請求使用
            localStorage.setItem('auth_token', token);
            onLogin(staff, token);
        } catch {
            setError('員工工號或密碼錯誤，請重試');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{
            minHeight:      '100vh',
            background:     'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #0a1628 100%)',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
        }}>
            <div style={{
                background:   'rgba(255,255,255,0.05)',
                border:       '1px solid rgba(0,170,255,0.3)',
                borderRadius: '20px',
                padding:      '40px',
                width:        '100%',
                maxWidth:     '400px',
                backdropFilter: 'blur(10px)',
            }}>
                {/* Logo 區 */}
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <p style={{ fontSize: 48, margin: 0 }}>🏬</p>
                    <h1 style={{ color: '#fff', margin: '8px 0 4px', fontSize: 22 }}>
                        PostureCoach Pro
                    </h1>
                    <p style={{ color: '#888', fontSize: 13, margin: 0 }}>
                        AI 智能姿勢訓練系統
                    </p>
                </div>

                <form onSubmit={handleLogin}>
                    {/* 工號輸入 */}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', color: '#888', fontSize: 12, marginBottom: 6 }}>
                            員工工號
                        </label>
                        <input
                            type="text"
                            value={employeeId}
                            onChange={e => setEmployeeId(e.target.value)}
                            placeholder="請輸入工號（如 EMP001）"
                            style={{
                                width:        '100%',
                                padding:      '12px 16px',
                                background:   'rgba(255,255,255,0.08)',
                                border:       '1px solid #333',
                                borderRadius: '8px',
                                color:        '#fff',
                                fontSize:     14,
                                boxSizing:    'border-box',
                                outline:      'none',
                            }}
                            onFocus={e  => (e.target.style.borderColor = '#00AAFF')}
                            onBlur={e   => (e.target.style.borderColor = '#333')}
                        />
                    </div>

                    {/* 密碼輸入 */}
                    <div style={{ marginBottom: 24 }}>
                        <label style={{ display: 'block', color: '#888', fontSize: 12, marginBottom: 6 }}>
                            密碼
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="請輸入密碼"
                            style={{
                                width:        '100%',
                                padding:      '12px 16px',
                                background:   'rgba(255,255,255,0.08)',
                                border:       '1px solid #333',
                                borderRadius: '8px',
                                color:        '#fff',
                                fontSize:     14,
                                boxSizing:    'border-box',
                                outline:      'none',
                            }}
                            onFocus={e  => (e.target.style.borderColor = '#00AAFF')}
                            onBlur={e   => (e.target.style.borderColor = '#333')}
                        />
                    </div>

                    {/* 錯誤提示 */}
                    {error && (
                        <p style={{
                            color:        '#FF4444',
                            fontSize:     12,
                            margin:       '0 0 16px',
                            padding:      '8px 12px',
                            background:   'rgba(255,68,68,0.1)',
                            borderRadius: '6px',
                            border:       '1px solid rgba(255,68,68,0.3)',
                        }}>
                            ⚠️ {error}
                        </p>
                    )}

                    {/* 登入按鈕 */}
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width:        '100%',
                            padding:      '14px',
                            background:   loading
                                ? '#333'
                                : 'linear-gradient(135deg, #0066cc, #00AAFF)',
                            border:       'none',
                            borderRadius: '10px',
                            color:        '#fff',
                            fontSize:     15,
                            fontWeight:   700,
                            cursor:       loading ? 'not-allowed' : 'pointer',
                            transition:   'opacity 0.2s',
                        }}
                    >
                        {loading ? '登入中...' : '登入 →'}
                    </button>
                </form>
            </div>
        </div>
    );
};

// ── AI 回饋彈窗 ──────────────────────────────────────────
interface FeedbackModalProps {
    feedback:  AIFeedbackResponse;
    onClose:   () => void;
}

const FeedbackModal: FC<FeedbackModalProps> = ({ feedback, onClose }) => (
    <div style={{
        position:       'fixed', inset: 0,
        background:     'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(4px)',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        zIndex:         1000,
        padding:        20,
    }}>
        <div style={{
            background:   '#1a1a2e',
            border:       '1px solid rgba(0,170,255,0.4)',
            borderRadius: '16px',
            padding:      '28px',
            maxWidth:     '600px',
            width:        '100%',
            maxHeight:    '80vh',
            overflow:     'auto',
        }}>
            <div style={{
                display:        'flex',
                justifyContent: 'space-between',
                alignItems:     'flex-start',
                marginBottom:   20,
            }}>
                <div>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: 18 }}>
                        🤖 AI 姿勢專科建議
                    </h3>
                    <p style={{ margin: '4px 0 0', color: '#888', fontSize: 12 }}>
                        由 Certified Posture Specialist AI 生成
                    </p>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        background:   'none',
                        border:       '1px solid #444',
                        borderRadius: '6px',
                        color:        '#888',
                        cursor:       'pointer',
                        padding:      '4px 10px',
                    }}
                >
                    ✕
                </button>
            </div>

            {/* AI 建議文字 */}
            <div style={{
                background:   'rgba(0,170,255,0.05)',
                borderLeft:   '3px solid #00AAFF',
                borderRadius: '0 8px 8px 0',
                padding:      '16px',
                marginBottom: 20,
                color:        '#ddd',
                fontSize:     13,
                lineHeight:   1.8,
                whiteSpace:   'pre-line',
            }}>
                {feedback.aiSuggestion}
            </div>

            {/* 知識卡片列表 */}
            {feedback.knowledgeCards.length > 0 && (
                <>
                    <h4 style={{ color: '#aaa', fontSize: 13, margin: '0 0 12px' }}>
                        📖 相關知識卡片
                    </h4>
                    {feedback.knowledgeCards.map((card, idx) => (
                        <div key={idx} style={{
                            background:   'rgba(255,255,255,0.04)',
                            borderRadius: '10px',
                            padding:      '14px',
                            marginBottom: 10,
                        }}>
                            <h5 style={{ color: '#00AAFF', margin: '0 0 8px', fontSize: 13 }}>
                                🔬 成因
                            </h5>
                            <p style={{ color: '#ccc', fontSize: 12, margin: '0 0 8px' }}>{card.cause}</p>
                            <h5 style={{ color: '#FF8C00', margin: '0 0 8px', fontSize: 13 }}>
                                ⚠️ 長期風險
                            </h5>
                            <p style={{ color: '#ccc', fontSize: 12, margin: '0 0 8px' }}>{card.risk}</p>
                            <h5 style={{ color: '#00FF88', margin: '0 0 8px', fontSize: 13 }}>
                                💡 工作提示
                            </h5>
                            <p style={{ color: '#ccc', fontSize: 12, margin: 0 }}>{card.standingTip}</p>
                        </div>
                    ))}
                </>
            )}

            <button
                onClick={onClose}
                style={{
                    width:        '100%',
                    padding:      '12px',
                    background:   'linear-gradient(135deg, #0066cc, #00AAFF)',
                    border:       'none',
                    borderRadius: '8px',
                    color:        '#fff',
                    fontSize:     14,
                    fontWeight:   600,
                    cursor:       'pointer',
                    marginTop:    8,
                }}
            >
                了解，繼續訓練
            </button>
        </div>
    </div>
);

// ── 根元件 ───────────────────────────────────────────────
const App: FC = () => {
    const [currentStaff,      setCurrentStaff]      = useState<Staff | null>(null);
    const [currentPage,       setCurrentPage]        = useState<Page>('camera');
    const [showFeedbackModal, setShowFeedbackModal]  = useState<boolean>(false);
    const [completedModules,  setCompletedModules]   = useState<string[]>([]);

    const { submitSession, lastFeedback, isSubmitting, error, clearError } = usePostureSession();

    // 從 localStorage 嘗試恢復登入狀態
    useEffect(() => {
        const token     = localStorage.getItem('auth_token');
        const staffJSON = localStorage.getItem('current_staff');

        if (token && staffJSON) {
            try {
                const staff = JSON.parse(staffJSON) as Staff;
                setCurrentStaff(staff);
            } catch {
                // 解析失敗則清除無效數據
                localStorage.removeItem('auth_token');
                localStorage.removeItem('current_staff');
            }
        }
    }, []);

    // 登入成功回調
    function handleLogin(staff: Staff, _token: string): void {
        setCurrentStaff(staff);
        localStorage.setItem('current_staff', JSON.stringify(staff));
    }

    // 登出
    function handleLogout(): void {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('current_staff');
        setCurrentStaff(null);
        setCurrentPage('camera');
    }

    // Session 完成回調（由 PostureCamera 觸發）
    async function handleSessionComplete(payload: PostureSessionPayload): Promise<void> {
        await submitSession(payload);
        setShowFeedbackModal(true); // 提交後顯示 AI 回饋彈窗
    }

    // 課程完成回調
    function handleModuleComplete(moduleId: string, quizScore: number): void {
        setCompletedModules(prev =>
            prev.includes(moduleId) ? prev : [...prev, moduleId]
        );
        console.log(`[App] 課程 ${moduleId} 完成，測驗分數：${quizScore}`);
    }

    // ── 未登入：顯示登入頁 ──────────────────────────────
    if (!currentStaff) {
        return <LoginPage onLogin={handleLogin} />;
    }

    // ── 已登入：主介面 ──────────────────────────────────
    return (
        <div style={{
            minHeight:  '100vh',
            background: '#0a0a1a',
            color:      '#fff',
            fontFamily: "'Noto Sans TC', -apple-system, sans-serif",
        }}>

            {/* ── 頂部導覽列 ─────────────────────────────── */}
            <header style={{
                background:   'rgba(26,26,46,0.95)',
                borderBottom: '1px solid #2a2a3e',
                padding:      '0 20px',
                position:     'sticky',
                top:          0,
                zIndex:       100,
                backdropFilter: 'blur(10px)',
            }}>
                <div style={{
                    maxWidth:       1200,
                    margin:         '0 auto',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'space-between',
                    height:         60,
                }}>
                    {/* Logo */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 22 }}>🏬</span>
                        <div>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>
                PostureCoach
              </span>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#00AAFF' }}>
                {' '}Pro
              </span>
                        </div>
                    </div>

                    {/* 導航連結（桌面版） */}
                    <nav style={{ display: 'flex', gap: 4 }}>
                        {NAV_ITEMS.map(item => (
                            <button
                                key={item.page}
                                onClick={() => setCurrentPage(item.page)}
                                style={{
                                    padding:      '6px 16px',
                                    borderRadius: '8px',
                                    border:       'none',
                                    background:   currentPage === item.page
                                        ? 'rgba(0,170,255,0.2)' : 'transparent',
                                    color:        currentPage === item.page ? '#00AAFF' : '#888',
                                    cursor:       'pointer',
                                    fontSize:     13,
                                    fontWeight:   currentPage === item.page ? 600 : 400,
                                    transition:   'all 0.2s',
                                }}
                            >
                                {item.icon} {item.label}
                            </button>
                        ))}
                    </nav>

                    {/* 員工資訊 + 登出 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ textAlign: 'right' }}>
                            <p style={{ margin: 0, fontSize: 13, color: '#fff' }}>{currentStaff.name}</p>
                            <p style={{ margin: 0, fontSize: 11, color: '#888' }}>
                                {currentStaff.department} · {currentStaff.floor}
                            </p>
                        </div>
                        <button
                            onClick={handleLogout}
                            style={{
                                padding:      '6px 12px',
                                background:   'none',
                                border:       '1px solid #444',
                                borderRadius: '6px',
                                color:        '#888',
                                cursor:       'pointer',
                                fontSize:     12,
                            }}
                        >
                            登出
                        </button>
                    </div>
                </div>
            </header>

            {/* ── 主內容區 ───────────────────────────────── */}
            <main style={{
                maxWidth: 1200,
                margin:   '0 auto',
                padding:  '24px 20px',
            }}>

                {/* 全域錯誤提示 */}
                {error && (
                    <div style={{
                        background:   'rgba(255,68,68,0.1)',
                        border:       '1px solid rgba(255,68,68,0.4)',
                        borderRadius: '8px',
                        padding:      '12px 16px',
                        marginBottom: 16,
                        display:      'flex',
                        justifyContent: 'space-between',
                        alignItems:   'center',
                    }}>
                        <span style={{ color: '#FF4444', fontSize: 13 }}>⚠️ {error}</span>
                        <button onClick={clearError}
                                style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}>
                            ✕
                        </button>
                    </div>
                )}

                {/* 提交載入提示 */}
                {isSubmitting && (
                    <div style={{
                        background:   'rgba(0,170,255,0.1)',
                        border:       '1px solid rgba(0,170,255,0.4)',
                        borderRadius: '8px',
                        padding:      '12px 16px',
                        marginBottom: 16,
                        textAlign:    'center',
                        color:        '#00AAFF',
                        fontSize:     13,
                    }}>
                        🤖 AI 正在分析您的姿勢並生成個人化建議，請稍候...
                    </div>
                )}

                {/* 頁面渲染 */}
                {currentPage === 'camera' && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 380px',
                        gap: 20,
                    }}>
                        <PostureCamera
                            staff={currentStaff}
                            onSessionComplete={handleSessionComplete}
                        />
                        {/* 右側快速導覽 */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{
                                background:   'rgba(255,255,255,0.04)',
                                borderRadius: '12px',
                                padding:      '20px',
                            }}>
                                <h4 style={{ margin: '0 0 12px', color: '#aaa', fontSize: 18 }}>
                                    📋 使用說明
                                </h4>
                                {[
                                    '確保攝影機能看到你的全身（頭到腰）',
                                    '站在距攝影機約 1-2 公尺處',
                                    '確保背景光線充足',
                                    '按「開始評估錄製」進行至少 10 秒的訓練',
                                    '按「完成並保存報告」獲取 AI 建議',
                                ].map((tip, idx) => (
                                    <div key={idx} style={{
                                        display:   'flex', gap: 10,
                                        marginBottom: 8, alignItems: 'flex-start',
                                        color: '#fff', fontSize: 16,
                                    }}>
                    <span style={{
                        background:   '#00AAFF',
                        color:        '#fff',
                        borderRadius: '50%',
                        width:        18, height: 18,
                        display:      'flex',
                        alignItems:   'center',
                        justifyContent: 'center',
                        fontSize:     14,
                        fontWeight:   700,
                        flexShrink:   0,
                    }}>
                      {idx + 1}
                    </span>
                                        <p style={{ margin: 0, color: '#FFF', fontSize: 14, lineHeight: 1.5 }}>
                                            {tip}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {currentPage === 'progress' && (
                    <ProgressDashboard
                        staffId={currentStaff.id}
                        staffName={currentStaff.name}
                    />
                )}

                {currentPage === 'learning' && (
                    <LearningModuleView
                        staffId={currentStaff.id}
                        completedModules={completedModules}
                        onModuleComplete={handleModuleComplete}
                    />
                )}

                {currentPage === 'exercises' && (
                    <ExerciseGuide filterByIssue={null} />
                )}
            </main>

            {/* ── AI 回饋彈窗 ────────────────────────────── */}
            {showFeedbackModal && lastFeedback && (
                <FeedbackModal
                    feedback={lastFeedback}
                    onClose={() => setShowFeedbackModal(false)}
                />
            )}
        </div>
    );
};

export default App;
